import { InlineFieldContext } from '@app/models';
import { Node, mergeAttributes } from '@tiptap/core';
import { Plugin } from 'prosemirror-state';

export const InlineField = Node.create({
  name: 'inlineField',
  group: 'inline',
  inline: true,
  draggable: true,

  addOptions() {
    return {
      handleInlineFieldClick: () => {},
      convertTextIntoFields: () => {},
      context: InlineFieldContext.Editor
    };
  },

  addAttributes() {
    return {
      name: {
        default: 'Field'
      },
      type: {
        default: 'shortTextField'
      },
      id: {
        default: ''
      },
      signer: {
        default: '0'
      },
      color: {
        default: '0'
      },
      selected: {
        default: null
      }
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-inline-field]'
      }
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(
        {
          'data-inline-field': 'true',
          class: (this.options.context === InlineFieldContext.FillSigner ? 'field--view' : 'field--inline') + ' forceDefaultColors',
          'data-field-name': HTMLAttributes['name'],
          'data-field-type': HTMLAttributes['type'],

          'data-field-id': HTMLAttributes['id'],

          'data-signer-id': HTMLAttributes['signer'],
          'data-color-id': HTMLAttributes['color'],

          'data-field-selected': HTMLAttributes['selected'],
          draggable: this.options.context === InlineFieldContext.Editor ? 'true' : 'false',
          contenteditable: 'false'
        },
        HTMLAttributes
      ),
      HTMLAttributes['name']
    ];
  },

  addNodeView() {
    return ({ node, getPos, editor }) => {
      const span = document.createElement('span');
      span.textContent = node.attrs['name'];
      span.className = (this.options.context === InlineFieldContext.FillSigner ? 'field--view' : 'field--inline') + ' forceDefaultColors';

      span.setAttribute('contenteditable', 'false');
      span.setAttribute('data-field-after', ' }}');

      span.setAttribute('data-field-name', node.attrs['name']);
      span.setAttribute('data-field-type', node.attrs['type']);

      if (node.attrs['id'] && node.attrs['id'] !== '') {
        span.setAttribute('data-field-id', node.attrs['id']);
      } else {
        span.removeAttribute('data-field-id');
      }

      span.setAttribute('data-signer-id', node.attrs['signer']);
      span.setAttribute('data-color-id', node.attrs['color']);

      if (this.options.context === InlineFieldContext.Editor) {
        if (node.attrs['selected']) {
          span.setAttribute('data-field-selected', node.attrs['selected']);
        }

        span.setAttribute('draggable', 'true');

        span.addEventListener(
          'dragstart',
          event => {
            event.dataTransfer?.setData('application/inline-field', JSON.stringify(node.attrs));
            event.stopPropagation();

            setTimeout(() => {
              const pos: any = getPos();
              editor
                .chain()
                .focus()
                .deleteRange({ from: pos, to: pos + node.nodeSize })
                .run();
            });
          },
          true
        );

        span.addEventListener('click', event => {
          this.options.handleInlineFieldClick(event);
        });
      }

      return {
        dom: span
      };
    };
  },

  addProseMirrorPlugins() {
    let shiftKeyPressed = false;

    return [
      new Plugin({
        props: {
          handleDOMEvents: {
            keydown: (view, event) => {
              if (event.key === 'Shift') shiftKeyPressed = true;
              return false;
            },
            keyup: (view, event) => {
              if (event.key === 'Shift') shiftKeyPressed = false;
              return false;
            },

            paste: view => {
              if (shiftKeyPressed) return;

              const state = view.state;
              const from = state.selection.from - 1;

              requestAnimationFrame(() => {
                const newState = view.state;
                const to = newState.selection.to + 1;

                this.options.convertTextIntoFields(true, { from, to });
              });
            }
          }
        }
      })
    ];
  }
});
